import React from "react";

interface Node {
  id: number;
  node_name: string;
  script_id?: number;
  node_group_id?: number;
  messages?: Record<string, string>;
  input_type?: string;
  input_data?: Record<string, any>;
  tracks?: Array<{ type: string; html_element: string }>;
  capture_rules?: Record<string, any>[];
  message_seq_no?: string;
}

interface NodeCardProps {
  node: Node;
  onEdit: (node: Node) => void;
  onDelete: (node: Node) => void;
  onClone: (node: Node) => void;
  scriptName?: string;
  // New props for transitions footer
  transitions?: Array<{ current_node_id: number; next_node_id: number }>;
  idToNameMap?: Record<number, string>;
  onTransitionClick?: (fromId: number, toId: number) => void;
}

export default function NodeCard({
  node,
  onEdit,
  onDelete,
  onClone,
  scriptName,
  transitions = [],
  idToNameMap = {},
  onTransitionClick,
}: NodeCardProps) {
  const isGlobal =
    (typeof (node as any).node_group_id === "number" &&
      Number.isFinite((node as any).node_group_id) &&
      (node as any).node_group_id > 0) ||
    !node.script_id;

  const getInputTypeLabel = (inputType?: string) => {
    const typeMap: Record<string, string> = {
      text: "Text Input",
      email: "Email Input",
      phone: "Phone Input",
      number: "Number Input",
      money: "Money Input",
      buttons: "Buttons",
      "date-picker": "Date Picker",
    };
    return inputType ? typeMap[inputType] || inputType : "No Input";
  };

  const getInputTypeColor = (inputType?: string) => {
    const colorMap: Record<string, string> = {
      text: "bg-blue-100 text-blue-800",
      email: "bg-green-100 text-green-800",
      phone: "bg-purple-100 text-purple-800",
      number: "bg-orange-100 text-orange-800",
      money: "bg-yellow-100 text-yellow-800",
      buttons: "bg-indigo-100 text-indigo-800",
      "date-picker": "bg-pink-100 text-pink-800",
    };
    return inputType
      ? colorMap[inputType] || "bg-gray-100 text-gray-800"
      : "bg-gray-100 text-gray-800";
  };

  const hasMessages = node.messages && Object.keys(node.messages).length > 0;
  const messageLanguages = hasMessages ? Object.keys(node.messages!) : [];
  const isSpecialName =
    typeof node?.node_name === "string" &&
    ["start", "end_conversation"].includes(node.node_name.toLowerCase());

  const outgoingNextIds = React.useMemo(() => {
    if (!transitions || transitions.length === 0) return [] as number[];
    const ids = transitions
      .filter((t) => t.current_node_id === node.id)
      .map((t) => t.next_node_id);
    return Array.from(new Set(ids));
  }, [transitions, node.id]);

  return (
    <div
      className={`rounded-lg border shadow-sm hover:shadow-md transition-shadow duration-200 ${isGlobal ? "bg-yellow-50 border-yellow-200" : "bg-white border-gray-200"}`}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 break-all whitespace-normal">
              {node.node_name}
            </h3>
          </div>

          {/* Action Menu */}
          <div className="flex gap-2">
            <button
              onClick={() => onEdit(node)}
              disabled={isSpecialName}
              className={`p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors ${isSpecialName ? "opacity-50 pointer-events-none" : ""}`}
              title="Edit node"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
            <button
              onClick={() => onClone(node)}
              disabled={isSpecialName}
              className={`p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors ${isSpecialName ? "opacity-50 pointer-events-none" : ""}`}
              title="Clone node"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </button>
            <button
              onClick={() => onDelete(node)}
              disabled={isSpecialName}
              className={`p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors ${isSpecialName ? "opacity-50 pointer-events-none" : ""}`}
              title="Delete node"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-3">
          {/* Script Association */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Script:</span>
            {node.script_id ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {scriptName || `Script ID: ${node.script_id}`}
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Global Node
              </span>
            )}
          </div>

          {/* Input Type */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Input Type:</span>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getInputTypeColor(node.input_type)}`}
            >
              {getInputTypeLabel(node.input_type)}
            </span>
          </div>

          {/* Messages */}
          {hasMessages && (
            <div>
              <span className="text-sm text-gray-500">Languages:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {messageLanguages.map((lang) => (
                  <span
                    key={lang}
                    className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800"
                  >
                    {lang.toUpperCase()}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Message Preview */}
          {hasMessages && node.messages?.en && (
            <div>
              <span className="text-sm text-gray-500">Message (EN):</span>
              <p className="text-sm text-gray-700 mt-1 break-words whitespace-pre-wrap">
                {node.messages.en.length > 100
                  ? `${node.messages.en.substring(0, 100)}...`
                  : node.messages.en}
              </p>
            </div>
          )}

          {/* Input Data Info */}
          {node.input_type === "buttons" && node.input_data?.options && (
            <div>
              <span className="text-sm text-gray-500">Buttons:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {node.input_data.options
                  .slice(0, 3)
                  .map((option: any, index: number) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-800"
                    >
                      {typeof option.text === "string"
                        ? option.text
                        : option.text?.en || option.value}
                    </span>
                  ))}
                {node.input_data.options.length > 3 && (
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                    +{node.input_data.options.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Tracking Info */}
          {node.tracks && node.tracks.length > 0 && (
            <div>
              <span className="text-sm text-gray-500">Tracking:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {node.tracks.map((track, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800"
                  >
                    {track.type}: {track.html_element}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Template Reference */}
          {/* Removed template_node_id and template_name usage */}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          {outgoingNextIds.length > 0 ? (
            <div className="text-sm text-gray-600">
              <span className="font-medium">Next:</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {outgoingNextIds.map((nextId) => (
                  <button
                    key={nextId}
                    type="button"
                    onClick={() =>
                      onTransitionClick && onTransitionClick(node.id, nextId)
                    }
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                    title={`Show ${node.node_name} → ${idToNameMap[nextId] || `Node ${nextId}`}`}
                  >
                    {idToNameMap[nextId] || `Node ${nextId}`}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-400">No transitions</div>
          )}
        </div>
      </div>
    </div>
  );
}
